import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
)

// ─── Public / Root ────────────────────────────────────────────────────────────
const HomePage = lazy(() => import('@/pages/home-page'))
const DashboardPage = lazy(() => import('@/pages/dashboard/dashboard-page'))
const LoginPage = lazy(() => import('@/pages/login/unified-login-page'))
const ProfilePage = lazy(() => import('@/pages/profile/profile-page'))
const VerifyEmailPage = lazy(() => import('@/pages/verify-email/verify-email-page'))
const VerifyRegistrationRedirect = lazy(() => import('@/pages/verify-registration/verify-registration-redirect'))
const CustomerRegisterPage = lazy(() => import('@/pages/customer-register/customer-register-page'))
const CartPage = lazy(() => import('@/pages/cart/cart-page'))
const OrdersPage = lazy(() => import('@/pages/orders/orders-page'))
const FulfilmentQueuePage = lazy(() => import('@/pages/orders/fulfilment-queue-page'))
const FailedOrdersPage = lazy(() => import('@/pages/orders/failed-orders-page'))
const ServicesPage = lazy(() => import('@/pages/services/services-page'))
const TreatmentsPage = lazy(() => import('@/pages/treatments/treatments-page'))
const NewTreatmentPage = lazy(() => import('@/pages/treatments/new/new-treatment-page'))
const TreatmentDetailPage = lazy(() => import('@/pages/treatments/_id/treatment-detail-page'))
const EditTreatmentPage = lazy(() => import('@/pages/treatments/_id/edit/edit-treatment-page'))
const SpaPackagesPage = lazy(() => import('@/pages/spa-packages/spa-packages-page'))
const NewSpaPackagePage = lazy(() => import('@/pages/spa-packages/new/new-spa-package-page'))
const SpaPackageDetailPage = lazy(() => import('@/pages/spa-packages/_id/spa-package-detail-page'))
const EditSpaPackagePage = lazy(() => import('@/pages/spa-packages/_id/edit/edit-spa-package-page'))
const VouchersPage = lazy(() => import('@/pages/vouchers/vouchers-page'))
const BookingConfirmationPage = lazy(() => import('@/pages/booking-confirmation/booking-confirmation-page'))

// ─── Auth ────────────────────────────────────────────────────────────────────
const ForgotPasswordPage = lazy(() => import('@/pages/auth/forgot-password/forgot-password-page'))
const ResetPasswordPage = lazy(() => import('@/pages/auth/reset-password/reset-password-page'))
const SocialSuccessPage = lazy(() => import('@/pages/auth/social/success/social-auth-success'))
const EntraCallbackPage = lazy(() => import('@/pages/auth/callback'))

// ─── Booking ─────────────────────────────────────────────────────────────────
const BookingSimplifiedSessionPage = lazy(() => import('@/pages/booking/simplified/_sessionTypeId/simplified-booking-page'))
const BookingSimplifiedTreatmentPage = lazy(() => import('@/pages/booking/simplified/treatment/_treatmentTypeId/simplified-treatment-booking-page'))
const BookingSimplifiedPackagePage = lazy(() => import('@/pages/booking/simplified/package/_packageId/simplified-package-book-page'))

// ─── Gift Vouchers ────────────────────────────────────────────────────────────
const GiftVoucherPurchasePage = lazy(() => import('@/pages/gift-vouchers/purchase/gift-voucher-purchase-page'))
const GiftVoucherConfirmationPage = lazy(() => import('@/pages/gift-vouchers/confirmation/voucher-confirmation-page'))

// ─── Payment ─────────────────────────────────────────────────────────────────
const PaymentPage = lazy(() => import('@/pages/payment/payment-page'))
const PaymentVerifonePage = lazy(() => import('@/pages/payment/verifone/verifone-payment-page'))
const PaymentCancelledPage = lazy(() => import('@/pages/payment/cancelled/payment-cancelled-page'))
const PaymentConfirmBookingPage = lazy(() => import('@/pages/payment/confirmation/booking/_bookingId/booking-payment-confirmation-page'))
const PaymentConfirmOrderPage = lazy(() => import('@/pages/payment/confirmation/order/_orderId/order-payment-confirmation-page'))
const PaymentConfirmReschedulePage = lazy(() => import('@/pages/payment/confirmation/reschedule/_bookingId/reschedule-payment-confirmation-page'))

// ─── Customer ────────────────────────────────────────────────────────────────
const CustomerDashboardPage = lazy(() => import('@/pages/customer/dashboard/customer-dashboard'))
const CustomerBookingsPage = lazy(() => import('@/pages/customer/bookings/customer-bookings-page'))
const CustomerOrdersPage = lazy(() => import('@/pages/customer/orders/customer-orders'))
const CustomerOrderDetailPage = lazy(() => import('@/pages/customer/orders/_id/order-confirmation-page'))
const CustomerPaymentsPage = lazy(() => import('@/pages/customer/payments/customer-payments'))
const CustomerPaymentMethodsPage = lazy(() => import('@/pages/customer/payment-methods/customer-payment-methods'))
const CustomerVouchersPage = lazy(() => import('@/pages/customer/vouchers/customer-vouchers'))
const CustomerHealthProfilePage = lazy(() => import('@/pages/customer/health/profile/customer-health-profile-page'))
const CustomerPreferencesPage = lazy(() => import('@/pages/customer/preferences/customer-preferences-page'))

// ─── Customers (staff) ────────────────────────────────────────────────────────
const CustomersPage = lazy(() => import('@/pages/customers/customers-page'))
const CustomerDetailPage = lazy(() => import('@/pages/customers/_id/customer-profile-page'))
const CustomerEditPage = lazy(() => import('@/pages/customers/_id/edit/edit-customer-page'))
const NewCustomerPage = lazy(() => import('@/pages/customers/new/new-customer-page'))

// ─── Data deletion ────────────────────────────────────────────────────────────
const DataDeletionConfirmationPage = lazy(() => import('@/pages/data-deletion/confirmation/data-deletion-confirmation'))

// ─── Staff ────────────────────────────────────────────────────────────────────
const StaffLoginPage = lazy(() => import('@/pages/staff/login/staff-login-page'))
const StaffDashboardPage = lazy(() => import('@/pages/staff/dashboard/staff-dashboard'))
const StaffBookingConfirmationPage = lazy(() => import('@/pages/staff/booking-confirmation/staff-booking-confirmation-page'))
const StaffAvailabilityPage = lazy(() => import('@/pages/staff/availability/staff-availability-page'))
const TherapistAvailabilityPage = lazy(() => import('@/pages/staff/availability/therapist-availability-page'))
const GroupAvailabilityPage = lazy(() => import('@/pages/staff/group-availability/group-availability-page'))

// ─── Platform Admin ───────────────────────────────────────────────────────────
const PlatformDashboardPage = lazy(() => import('@/pages/platform/dashboard/platform-dashboard'))
const PlatformSettingsPage = lazy(() => import('@/pages/platform/settings/platform-settings-page'))
const PlatformTenantsPage = lazy(() => import('@/pages/platform/tenants/tenants-page'))
const PlatformTenantDetailPage = lazy(() => import('@/pages/platform/tenants/_id/tenant-detail-page'))
const PlatformTenantEditPage = lazy(() => import('@/pages/platform/tenants/_id/edit/tenant-edit-page'))
const PlatformUsersPage = lazy(() => import('@/pages/platform/users/platform-users-page'))
const PlatformUserNewPage = lazy(() => import('@/pages/platform/users/new/new-user-page'))
const PlatformUserDetailPage = lazy(() => import('@/pages/platform/users/_id/user-detail-page'))
const PlatformUserEditPage = lazy(() => import('@/pages/platform/users/_id/edit/edit-user-page'))

// ─── Admin (Practitioners) ────────────────────────────────────────────────────
const PractitionersPage = lazy(() => import('@/pages/admin/practitioners/practitioners-page'))
const PractitionerNewPage = lazy(() => import('@/pages/admin/practitioners/new/new-practitioner-page'))
const PractitionerDetailPage = lazy(() => import('@/pages/admin/practitioners/_id/practitioner-detail-page'))
const PractitionerEditPage = lazy(() => import('@/pages/admin/practitioners/_id/edit/practitioner-edit-page'))
const TherapistSchedulesPage = lazy(() => import('@/pages/admin/practitioners/therapist-schedules-page'))
const QualificationMatrixPage = lazy(() => import('@/pages/admin/practitioners/qualification-matrix-page'))

// ─── Tenant Admin ─────────────────────────────────────────────────────────────
const TenantDashboardPage = lazy(() => import('@/pages/tenant/dashboard/tenant-dashboard'))
const ScheduleStatusHistoryPage = lazy(() => import('@/pages/tenant/schedule-status-history/schedule-status-history-page'))
const TenantAdminPage = lazy(() => import('@/pages/tenant/admin/admin-dashboard'))
const TenantSettingsPage = lazy(() => import('@/pages/tenant/settings/tenant-settings-page'))
const ShiftConfigurationPage = lazy(() => import('@/pages/tenant/settings/shift-configuration-page'))
const TenantBookingsPage = lazy(() => import('@/pages/tenant/bookings/tenant-bookings-page'))
const TenantSessionsPage = lazy(() => import('@/pages/tenant/sessions/tenant-sessions-page'))
const TenantCategoriesPage = lazy(() => import('@/pages/tenant/categories/categories-page'))
const NewSessionTypePage = lazy(() => import('@/pages/tenant/sessions/new/new-session-type-page'))
const SessionTypeDetailPage = lazy(() => import('@/pages/tenant/sessions/_id/session-type-detail-page'))
const EditSessionTypePage = lazy(() => import('@/pages/tenant/sessions/_id/edit/edit-session-type-page'))
const TenantPaymentsPage = lazy(() => import('@/pages/tenant/payments/tenant-payments'))
const TenantReportsPage = lazy(() => import('@/pages/tenant/reports/reports-page'))
const TenantEmailTemplatesPage = lazy(() => import('@/pages/tenant/email-templates/email-templates-page'))
const TenantBlockedAccountsPage = lazy(() => import('@/pages/tenant/blocked-accounts/staff-blocked-accounts-page'))
const TenantInventoryPage = lazy(() => import('@/pages/tenant/inventory/inventory-redirect-page'))
const TenantInventoryProductsPage = lazy(() => import('@/pages/tenant/inventory/products/inventory-management-page'))
const TenantUserManualPage = lazy(() => import('@/pages/tenant/help/user-manual/user-manual-page'))
const TenantDiscountTypesPage = lazy(() => import('@/pages/tenant/discount-types/discount-types-page'))
const TenantPostingCodesPage = lazy(() => import('@/pages/tenant/posting-codes/posting-codes-page'))
const TenantPaymentMethodsPage = lazy(() => import('@/pages/tenant/payment-methods/payment-methods-page'))
const NewDiscountTypePage = lazy(() => import('@/pages/tenant/discount-types/new/new-discount-type-page'))
const EditDiscountTypePage = lazy(() => import('@/pages/tenant/discount-types/_id/edit/edit-discount-type-page'))

// ─── Tenant Staff ─────────────────────────────────────────────────────────────
const TenantStaffPage = lazy(() => import('@/pages/tenant/staff/tenant-staff-page'))
const TenantStaffNewPage = lazy(() => import('@/pages/tenant/staff/new/new-staff-page'))
const TenantStaffDetailPage = lazy(() => import('@/pages/tenant/staff/_id/staff-detail-page'))
const TenantStaffEditPage = lazy(() => import('@/pages/tenant/staff/_id/edit/edit-staff-page'))

// ─── Tenant Sites ─────────────────────────────────────────────────────────────
const TenantSitesPage = lazy(() => import('@/pages/tenant/sites/sites-page'))
const TenantSiteNewPage = lazy(() => import('@/pages/tenant/sites/new/new-site-page'))
const TenantSiteDetailPage = lazy(() => import('@/pages/tenant/sites/_id/site-detail-page'))
const TenantSiteEditPage = lazy(() => import('@/pages/tenant/sites/_id/edit/edit-site-page'))

// ─── Tenant Treatment Rooms ───────────────────────────────────────────────────
const TenantTreatmentRoomsPage = lazy(() => import('@/pages/tenant/treatment-rooms/treatment-rooms-page'))
const TenantTreatmentRoomNewPage = lazy(() => import('@/pages/tenant/treatment-rooms/new/new-treatment-room-page'))
const TenantTreatmentRoomSchedulePage = lazy(() => import('@/pages/tenant/treatment-rooms/schedule/room-schedule-page'))
const TenantTreatmentRoomAnalyticsPage = lazy(() => import('@/pages/tenant/treatment-rooms/analytics/room-analytics-page'))
const TenantTreatmentRoomDetailPage = lazy(() => import('@/pages/tenant/treatment-rooms/_id/treatment-room-detail-page'))
const TenantTreatmentRoomEditPage = lazy(() => import('@/pages/tenant/treatment-rooms/_id/edit/edit-treatment-room-page'))
const TenantTreatmentRoomEquipmentPage = lazy(() => import('@/pages/tenant/treatment-rooms/_id/equipment/room-equipment-page'))
const TenantTreatmentRoomMaintenancePage = lazy(() => import('@/pages/tenant/treatment-rooms/_id/maintenance/room-maintenance-page'))
const TenantMaintenanceSchedulePage = lazy(() => import('@/pages/tenant/treatment-rooms/maintenance/maintenance-schedule-page'))

// ─── Tenant Room Types ─────────────────────────────────────────────────────────
const TenantRoomTypesPage = lazy(() => import('@/pages/tenant/room-types/room-types-page'))
const NewRoomTypePage = lazy(() => import('@/pages/tenant/room-types/new/new-room-type-page'))
const EditRoomTypePage = lazy(() => import('@/pages/tenant/room-types/_id/edit/edit-room-type-page'))

// ─── Tenant Health Questionnaires ─────────────────────────────────────────────
const TenantHealthQuestionnairesPage = lazy(() => import('@/pages/tenant/health-questionnaires/health-questionnaire-dashboard'))
const TenantHealthQuestionnairesQuestionsPage = lazy(() => import('@/pages/tenant/health-questionnaires/questions/health-questionnaires-page'))
const TenantHealthAssessmentFormsPage = lazy(() => import('@/pages/tenant/health-questionnaires/forms/health-assessment-forms-page'))
const TenantHealthFormEditorPage = lazy(() => import('@/pages/tenant/health-questionnaires/forms/health-form-editor-page'))
const TenantHealthFormAssignmentsPage = lazy(() => import('@/pages/tenant/health-questionnaires/assignments/form-assignments-page'))
const TenantHealthQuestionnairesAssignmentsPage = lazy(() => import('@/pages/tenant/health-questionnaires/assignments/questionnaire-assignments-page'))

// ─── Tenant POS ───────────────────────────────────────────────────────────────
const TenantPOSPage = lazy(() => import('@/pages/tenant/pos/pos-redirect-page'))
const TenantPOSReceptionPage = lazy(() => import('@/pages/tenant/pos/reception/reception-page'))
const TenantPOSReservationPage = lazy(() => import('@/pages/tenant/pos/reservation/reservation-page'))
const TenantPOSTherapyPage = lazy(() => import('@/pages/tenant/pos/therapy/therapy-page'))
const TenantPOSRestaurantPage = lazy(() => import('@/pages/tenant/pos/restaurant/restaurant-page'))
const TenantPOSShopPage = lazy(() => import('@/pages/tenant/pos/shop/shop-page'))

export function AppRouter() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Root */}
        <Route index element={<HomePage />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="login" element={<LoginPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="verify-email" element={<VerifyEmailPage />} />
        <Route path="verify-registration" element={<VerifyRegistrationRedirect />} />
        <Route path="customer-register" element={<CustomerRegisterPage />} />
        <Route path="cart" element={<CartPage />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="orders/fulfilment" element={<FulfilmentQueuePage />} />
        <Route path="orders/failed" element={<FailedOrdersPage />} />
        <Route path="services" element={<ServicesPage />} />
        <Route path="treatments">
          <Route index element={<TreatmentsPage />} />
          <Route path="new" element={<NewTreatmentPage />} />
          <Route path=":id/edit" element={<EditTreatmentPage />} />
          <Route path=":id" element={<TreatmentDetailPage />} />
        </Route>
        <Route path="spa-packages">
          <Route index element={<SpaPackagesPage />} />
          <Route path="new" element={<NewSpaPackagePage />} />
          <Route path=":id/edit" element={<EditSpaPackagePage />} />
          <Route path=":id" element={<SpaPackageDetailPage />} />
        </Route>
        <Route path="vouchers" element={<VouchersPage />} />
        <Route path="booking-confirmation" element={<BookingConfirmationPage />} />

        {/* Auth */}
        <Route path="auth">
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route path="reset-password" element={<ResetPasswordPage />} />
          <Route path="social/success" element={<SocialSuccessPage />} />
          <Route path="callback" element={<EntraCallbackPage />} />
        </Route>

        {/* Booking */}
        <Route path="booking/simplified">
          <Route path=":sessionTypeId" element={<BookingSimplifiedSessionPage />} />
          <Route path="treatment/:treatmentTypeId" element={<BookingSimplifiedTreatmentPage />} />
          <Route path="package/:packageId" element={<BookingSimplifiedPackagePage />} />
        </Route>

        {/* Gift Vouchers */}
        <Route path="gift-vouchers">
          <Route path="purchase" element={<GiftVoucherPurchasePage />} />
          <Route path="confirmation" element={<GiftVoucherConfirmationPage />} />
        </Route>

        {/* Payment */}
        <Route path="payment">
          <Route index element={<PaymentPage />} />
          <Route path="verifone" element={<PaymentVerifonePage />} />
          <Route path="cancelled" element={<PaymentCancelledPage />} />
          <Route path="confirmation">
            <Route path="booking/:bookingId" element={<PaymentConfirmBookingPage />} />
            <Route path="order/:orderId" element={<PaymentConfirmOrderPage />} />
            <Route path="reschedule/:bookingId" element={<PaymentConfirmReschedulePage />} />
          </Route>
        </Route>

        {/* Customer (self-service) */}
        <Route path="customer">
          <Route path="dashboard" element={<CustomerDashboardPage />} />
          <Route path="bookings" element={<CustomerBookingsPage />} />
          <Route path="orders">
            <Route index element={<CustomerOrdersPage />} />
            <Route path=":id" element={<CustomerOrderDetailPage />} />
          </Route>
          <Route path="payments" element={<CustomerPaymentsPage />} />
          <Route path="payment-methods" element={<CustomerPaymentMethodsPage />} />
          <Route path="vouchers" element={<CustomerVouchersPage />} />
          <Route path="health/profile" element={<CustomerHealthProfilePage />} />
          <Route path="preferences" element={<CustomerPreferencesPage />} />
        </Route>

        {/* Customers management (staff) */}
        <Route path="customers">
          <Route index element={<CustomersPage />} />
          <Route path="new" element={<NewCustomerPage />} />
          <Route path=":id">
            <Route index element={<CustomerDetailPage />} />
            <Route path="edit" element={<CustomerEditPage />} />
          </Route>
        </Route>

        {/* Data deletion */}
        <Route path="data-deletion/confirmation" element={<DataDeletionConfirmationPage />} />

        {/* Staff */}
        <Route path="staff">
          <Route path="login" element={<StaffLoginPage />} />
          <Route path="dashboard" element={<StaffDashboardPage />} />
          <Route path="booking-confirmation" element={<StaffBookingConfirmationPage />} />
          <Route path="availability" element={<StaffAvailabilityPage />} />
          <Route path="therapist-availability" element={<TherapistAvailabilityPage />} />
          <Route path="group-availability" element={<GroupAvailabilityPage />} />
        </Route>

        {/* Platform Admin */}
        <Route path="platform">
          <Route path="dashboard" element={<PlatformDashboardPage />} />
          <Route path="settings" element={<PlatformSettingsPage />} />
          <Route path="tenants">
            <Route index element={<PlatformTenantsPage />} />
            <Route path=":id">
              <Route index element={<PlatformTenantDetailPage />} />
              <Route path="edit" element={<PlatformTenantEditPage />} />
            </Route>
          </Route>
          <Route path="users">
            <Route index element={<PlatformUsersPage />} />
            <Route path="new" element={<PlatformUserNewPage />} />
            <Route path=":id">
              <Route index element={<PlatformUserDetailPage />} />
              <Route path="edit" element={<PlatformUserEditPage />} />
            </Route>
          </Route>
        </Route>

        {/* Admin (Practitioners) */}
        <Route path="admin/practitioners">
          <Route index element={<PractitionersPage />} />
          <Route path="new" element={<PractitionerNewPage />} />
          <Route path="schedules" element={<TherapistSchedulesPage />} />
          <Route path="shifts" element={<Navigate to="/tenant/settings/shifts" replace />} />
          <Route path="qualifications" element={<QualificationMatrixPage />} />
          <Route path=":id">
            <Route index element={<PractitionerDetailPage />} />
            <Route path="edit" element={<PractitionerEditPage />} />
          </Route>
        </Route>

        {/* Tenant Admin */}
        <Route path="tenant">
          <Route path="dashboard" element={<TenantDashboardPage />} />
          <Route path="schedule-status-history" element={<ScheduleStatusHistoryPage />} />
          <Route path="admin" element={<TenantAdminPage />} />
          <Route path="settings" element={<TenantSettingsPage />} />
          <Route path="posting-codes">
            <Route index element={<TenantPostingCodesPage />} />
          </Route>
          <Route path="payment-methods">
            <Route index element={<TenantPaymentMethodsPage />} />
          </Route>
          <Route path="discount-types">
            <Route index element={<TenantDiscountTypesPage />} />
            <Route path="new" element={<NewDiscountTypePage />} />
            <Route path=":id/edit" element={<EditDiscountTypePage />} />
          </Route>
          <Route path="settings/shifts" element={<ShiftConfigurationPage />} />
          <Route path="bookings" element={<TenantBookingsPage />} />
          <Route path="sessions">
            <Route index element={<TenantSessionsPage />} />
            <Route path="new" element={<NewSessionTypePage />} />
            <Route path=":id" element={<SessionTypeDetailPage />} />
            <Route path=":id/edit" element={<EditSessionTypePage />} />
          </Route>
          <Route path="categories" element={<TenantCategoriesPage />} />
          <Route path="payments" element={<TenantPaymentsPage />} />
          <Route path="reports" element={<TenantReportsPage />} />
          <Route path="email-templates" element={<TenantEmailTemplatesPage />} />
          <Route path="blocked-accounts" element={<TenantBlockedAccountsPage />} />
          <Route path="help/user-manual" element={<TenantUserManualPage />} />

          {/* Inventory */}
          <Route path="inventory">
            <Route index element={<TenantInventoryPage />} />
            <Route path="products" element={<TenantInventoryProductsPage />} />
          </Route>

          {/* Staff */}
          <Route path="staff">
            <Route index element={<TenantStaffPage />} />
            <Route path="new" element={<TenantStaffNewPage />} />
            <Route path=":id">
              <Route index element={<TenantStaffDetailPage />} />
              <Route path="edit" element={<TenantStaffEditPage />} />
            </Route>
          </Route>

          {/* Sites */}
          <Route path="sites">
            <Route index element={<TenantSitesPage />} />
            <Route path="new" element={<TenantSiteNewPage />} />
            <Route path=":id">
              <Route index element={<TenantSiteDetailPage />} />
              <Route path="edit" element={<TenantSiteEditPage />} />
            </Route>
          </Route>

          {/* Treatment Rooms */}
          <Route path="treatment-rooms">
            <Route index element={<TenantTreatmentRoomsPage />} />
            <Route path="new" element={<TenantTreatmentRoomNewPage />} />
            <Route path="schedule" element={<TenantTreatmentRoomSchedulePage />} />
            <Route path="analytics" element={<TenantTreatmentRoomAnalyticsPage />} />
            <Route path="maintenance" element={<TenantMaintenanceSchedulePage />} />
            <Route path=":id">
              <Route index element={<TenantTreatmentRoomDetailPage />} />
              <Route path="edit" element={<TenantTreatmentRoomEditPage />} />
              <Route path="equipment" element={<TenantTreatmentRoomEquipmentPage />} />
              <Route path="maintenance" element={<TenantTreatmentRoomMaintenancePage />} />
            </Route>
          </Route>

          {/* Room Types */}
          <Route path="room-types">
            <Route index element={<TenantRoomTypesPage />} />
            <Route path="new" element={<NewRoomTypePage />} />
            <Route path=":id/edit" element={<EditRoomTypePage />} />
          </Route>

          {/* Health Questionnaires */}
          <Route path="health-questionnaires">
            <Route index element={<TenantHealthQuestionnairesPage />} />
            <Route path="forms" element={<TenantHealthAssessmentFormsPage />} />
            <Route path="forms/:formId" element={<TenantHealthFormEditorPage />} />
            <Route path="form-assignments" element={<TenantHealthFormAssignmentsPage />} />
            <Route path="questions" element={<TenantHealthQuestionnairesQuestionsPage />} />
            <Route path="assignments" element={<TenantHealthQuestionnairesAssignmentsPage />} />
          </Route>

          {/* POS */}
          <Route path="pos">
            <Route index element={<TenantPOSPage />} />
            <Route path="reception" element={<TenantPOSReceptionPage />} />
            <Route path="reservation" element={<TenantPOSReservationPage />} />
            <Route path="therapy" element={<TenantPOSTherapyPage />} />
            <Route path="restaurant" element={<TenantPOSRestaurantPage />} />
            <Route path="shop" element={<TenantPOSShopPage />} />
          </Route>
        </Route>

        {/* Catch-all → home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}
